import { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";

import StandardCard from "./StandardCard";

import { buildIncludedKeySet } from "./differentiationHierarchyPanelUtils";

const DifferentiationHierarchyPanel = ({
    dataType,
    selectedTemplate,
    tree = [],
    selectedKeys,
    selectedCount = 0,
    onToggle
}) => {
    const [openStandards, setOpenStandards] = useState(() => new Set());
    const includedKeys = useMemo(() => buildIncludedKeySet(selectedTemplate), [selectedTemplate]);

    const toggleStandard = useCallback((stdId) => {
        setOpenStandards((prev) => {
            const s = new Set(prev);
            s.has(stdId) ? s.delete(stdId) : s.add(stdId);
            return s;
        });
    }, []);

    const expandAll = useCallback(() => {
        setOpenStandards(new Set(tree.map((s) => s.id ?? s.key ?? s.standardName)));
    }, [tree]);

    const collapseAll = useCallback(() => setOpenStandards(new Set()), []);

    const hasTree = Array.isArray(tree) && tree.length > 0;

    return (
        <section className="diff-content-section">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-3">
                    {dataType === "DIFF" && (
                        <div className="text-muted small">
                            {selectedCount} content item{selectedCount === 1 ? "" : "s"} selected across all grades
                        </div>
                    )}
                    {hasTree && (
                        <div className="btn-group btn-group-sm" role="group" aria-label="Expand/Collapse standards">
                            <button type="button" className="btn btn-outline-secondary" onClick={expandAll}>
                                Expand all
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={collapseAll}>
                                Collapse all
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {hasTree &&
                tree.map((std) => {
                    const stdId = std.id ?? std.key ?? std.standardName;
                    const isOpen = openStandards.has(stdId);
                    return (
                        <StandardCard
                            key={stdId}
                            dataType={dataType}
                            includedKeys={includedKeys}
                            node={std}
                            isOpen={isOpen}
                            onToggle={() => toggleStandard(stdId)}
                            selectedKeys={selectedKeys}
                            onToggleItem={onToggle}
                        />
                    );
                })}
        </section>
    );
};

DifferentiationHierarchyPanel.propTypes = {
    dataType: PropTypes.string,
    selectedTemplate: PropTypes.object,
    tree: PropTypes.array,
    selectedKeys: PropTypes.instanceOf(Set),
    selectedCount: PropTypes.number,
    onToggle: PropTypes.func
};

DifferentiationHierarchyPanel.defaultProps = {
    dataType: "",
    selectedTemplate: null,
    tree: [],
    selectedKeys: new Set(),
    selectedCount: 0,
    onToggle: () => {}
};

export default DifferentiationHierarchyPanel;
